import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Room, RoomEvent, Track, type RemoteAudioTrack } from 'livekit-client';
import { createCall, endCall, fetchCallSession } from '@api/crm';
import type { CRMCall, CRMCallCreatePayload } from '@/types/api';

type LiveCallStatus = 'idle' | 'connecting' | 'connected' | 'disconnecting' | 'error';

type ActiveCallState = {
  call: CRMCall;
  status: LiveCallStatus;
  room?: Room;
  remoteAudioTracks: RemoteAudioTrack[];
  startedAt?: number;
  isMuted: boolean;
  errorMessage?: string | null;
};

type CallContextValue = {
  activeCall: ActiveCallState | null;
  isStarting: boolean;
  startLiveCall: (payload: CRMCallCreatePayload) => Promise<void>;
  endLiveCall: () => Promise<void>;
  toggleMute: () => Promise<void>;
  clearCallError: () => void;
};

const CallContext = createContext<CallContextValue | null>(null);

function getDurationSeconds(startedAt?: number) {
  if (!startedAt) {
    return null;
  }
  return Math.max(Math.round((Date.now() - startedAt) / 1000), 0);
}

export function CallProvider({ children }: PropsWithChildren) {
  const queryClient = useQueryClient();
  const [activeCall, setActiveCall] = useState<ActiveCallState | null>(null);
  const [isStarting, setIsStarting] = useState(false);
  const finalizedCallIdRef = useRef<string | null>(null);

  const finalizeCall = useCallback(
    async (call: CRMCall, startedAt?: number, status: 'completed' | 'failed' = 'completed', failureReason?: string) => {
      if (finalizedCallIdRef.current === call.id) {
        return;
      }
      finalizedCallIdRef.current = call.id;
      try {
        await endCall(call.id, {
          status,
          ended_at: new Date().toISOString(),
          duration_seconds: getDurationSeconds(startedAt),
          failure_reason: failureReason ?? null,
          recording_status: call.recording_status,
          transcript_status: call.transcript_status,
          processing_status: call.processing_status,
        });
      } catch (error) {
        console.error('Failed to finalize CRM call', error);
      } finally {
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: ['crm', 'calls'] }),
          queryClient.invalidateQueries({ queryKey: ['crm', 'call', call.id] }),
          call.lead_id
            ? queryClient.invalidateQueries({ queryKey: ['crm', 'activities', 'lead', call.lead_id] })
            : Promise.resolve(),
          call.account_id
            ? queryClient.invalidateQueries({ queryKey: ['crm', 'activities', 'account', call.account_id] })
            : Promise.resolve(),
        ]);
      }
    },
    [queryClient],
  );

  const cleanupRoom = useCallback((room?: Room) => {
    if (!room) {
      return;
    }
    room.removeAllListeners();
    if (room.state !== 'disconnected') {
      room.disconnect();
    }
  }, []);

  const startLiveCall = useCallback(async (payload: CRMCallCreatePayload) => {
    if (activeCall || isStarting) {
      return;
    }

    setIsStarting(true);
    finalizedCallIdRef.current = null;

    try {
      const createdCall = await createCall({
        ...payload,
        initiate_provider_call: true,
        status: 'queued',
      });

      if (!createdCall.provider_room_name) {
        throw new Error('Call room was not created by the backend.');
      }

      const session = await fetchCallSession(createdCall.provider_room_name);
      if (!session.available || !session.ws_url || !session.access_token) {
        throw new Error(
          session.missing_configuration[0] ?? 'LiveKit session is unavailable for this call.',
        );
      }

      const room = new Room({
        adaptiveStream: true,
        dynacast: true,
      });

      const nextState: ActiveCallState = {
        call: createdCall,
        status: 'connecting',
        room,
        remoteAudioTracks: [],
        startedAt: undefined,
        isMuted: false,
        errorMessage: null,
      };
      setActiveCall(nextState);
      await queryClient.invalidateQueries({ queryKey: ['crm', 'calls'] });

      room.on(RoomEvent.TrackSubscribed, (track) => {
        if (track.kind !== Track.Kind.Audio) {
          return;
        }
        setActiveCall((current) => {
          if (!current) {
            return current;
          }
          const audioTrack = track as RemoteAudioTrack;
          if (current.remoteAudioTracks.some((candidate) => candidate.sid === audioTrack.sid)) {
            return current;
          }
          return {
            ...current,
            remoteAudioTracks: [...current.remoteAudioTracks, audioTrack],
          };
        });
      });

      room.on(RoomEvent.TrackUnsubscribed, (track) => {
        if (track.kind !== Track.Kind.Audio) {
          return;
        }
        setActiveCall((current) => {
          if (!current) {
            return current;
          }
          return {
            ...current,
            remoteAudioTracks: current.remoteAudioTracks.filter((candidate) => candidate.sid !== track.sid),
          };
        });
      });

      room.on(RoomEvent.ParticipantDisconnected, () => {
        setActiveCall((current) => {
          if (!current) {
            return current;
          }
          void finalizeCall(current.call, current.startedAt, 'completed');
          cleanupRoom(current.room);
          return null;
        });
      });

      room.on(RoomEvent.Disconnected, () => {
        setActiveCall((current) => {
          if (!current) {
            return current;
          }
          if (finalizedCallIdRef.current !== current.call.id) {
            void finalizeCall(current.call, current.startedAt, 'completed');
          }
          return null;
        });
      });

      await room.connect(session.ws_url, session.access_token);
      await room.localParticipant.setMicrophoneEnabled(true);

      setActiveCall((current) => {
        if (!current) {
          return current;
        }
        return {
          ...current,
          status: 'connected',
          startedAt: Date.now(),
        };
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to start live call.';
      setActiveCall((current) => {
        if (!current) {
          return {
            call: {
              id: 'uninitialized',
              provider: 'livekit',
              direction: 'outbound',
              status: 'failed',
              to_number: payload.to_number,
              recording_status: payload.recording_requested ? 'pending' : 'not_requested',
              transcript_status: payload.recording_requested ? 'pending' : 'not_requested',
              processing_status: payload.recording_requested ? 'pending' : 'not_requested',
              consent_status: payload.consent_status ?? 'unknown',
              created_at: new Date().toISOString(),
            },
            status: 'error',
            remoteAudioTracks: [],
            isMuted: false,
            errorMessage: message,
          };
        }
        cleanupRoom(current.room);
        return {
          ...current,
          status: 'error',
          errorMessage: message,
        };
      });
    } finally {
      setIsStarting(false);
    }
  }, [activeCall, cleanupRoom, finalizeCall, isStarting, queryClient]);

  const endLiveCall = useCallback(async () => {
    if (!activeCall) {
      return;
    }

    setActiveCall((current) => (current ? { ...current, status: 'disconnecting' } : current));
    await finalizeCall(activeCall.call, activeCall.startedAt, 'completed');
    cleanupRoom(activeCall.room);
    setActiveCall(null);
  }, [activeCall, cleanupRoom, finalizeCall]);

  const toggleMute = useCallback(async () => {
    if (!activeCall?.room) {
      return;
    }
    const nextMuted = !activeCall.isMuted;
    await activeCall.room.localParticipant.setMicrophoneEnabled(!nextMuted);
    setActiveCall((current) => (current ? { ...current, isMuted: nextMuted } : current));
  }, [activeCall]);

  const clearCallError = useCallback(() => {
    setActiveCall((current) => (current ? { ...current, errorMessage: null } : null));
  }, []);

  useEffect(() => () => cleanupRoom(activeCall?.room), [activeCall?.room, cleanupRoom]);

  const value = useMemo<CallContextValue>(
    () => ({
      activeCall,
      isStarting,
      startLiveCall,
      endLiveCall,
      toggleMute,
      clearCallError,
    }),
    [activeCall, clearCallError, endLiveCall, isStarting, startLiveCall, toggleMute],
  );

  return <CallContext.Provider value={value}>{children}</CallContext.Provider>;
}

export function useCallProvider() {
  const context = useContext(CallContext);
  if (!context) {
    throw new Error('useCallProvider must be used inside CallProvider');
  }
  return context;
}
